import { PhotoLibrary, Close, AddAPhoto, ChildCare } from "@mui/icons-material";
import { useEffect } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  ImageList,
  ImageListItem,
  MenuItem,
  IconButton,
  Select,
  TextField,
  Grid,
  InputLabel,
  FormControl,
  OutlinedInput,
  Typography,
  alpha,
  useTheme,
  CircularProgress,
  Slide,
} from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import * as React from "react";
import { createEvent } from "apis";
import { useSnackbar } from "notistack";
import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "store";
import { addEvent, invalidateEvents } from "store/features/events";
import { IEventType } from "./Event";
import dayjs, { Dayjs } from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DesktopTimePicker } from "@mui/x-date-pickers";
import Axios from "axios";
import ChildPickerOption, {
  ChildAvatar,
  childPickerMenuItemSx,
  childPickerMenuProps,
} from "components/utils/ChildPickerOption";

const SlideUp = React.forwardRef(function SlideUp(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

interface EventDialogProps {
  open: boolean;
  onClose: () => void;
  child: any[];
  event: string;
  editItem?: any;
}

const EventDialog: React.FC<EventDialogProps> = ({
  open,
  onClose,
  child,
  event,
  editItem,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { register, handleSubmit, reset } = useForm<any>();
  const [images, setImages] = useState<File[]>([]);
  const [imgs, setImgs] = useState<string[]>([]);
  const [childrenSelected, setChildrenSelected] = useState<string>();
  const [submitting, setSubmitting] = useState(false);

  const eventTypes = useSelector<RootState, IEventType[]>(
    (store) => store.events.eventTypes,
  );
  const eventTypeId = event
    ? eventTypes.find(({ name }) => name.toLowerCase() === event.toLowerCase())
        ?.id
    : null;
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();

  const [sleepFrom, setSleepFrom] = React.useState<Dayjs | null>(
    dayjs().startOf("hour"),
  );
  const [sleepTo, setSleepTo] = React.useState<Dayjs | null>(
    dayjs().startOf("hour").add(1, "hour"),
  );

  const isEditing = Boolean(editItem);
  const eventLower = (event ?? "observation").toLowerCase();
  const showNotes = event !== "Meal";
  const showPhotos = event === "Photos" || imgs.length > 0 || images.length > 0;

  useEffect(() => {
    if (editItem) {
      setChildrenSelected(editItem.child.id);
      setImgs(editItem.images || []);
      reset(editItem.infos);
    } else {
      setChildrenSelected(child?.[0]?.id);
      setImgs([]);
      reset({});
    }
  }, [editItem, reset, child]);

  const handleFormSubmit = (data: any) => {
    if (!childrenSelected) {
      enqueueSnackbar(t("placeholders.select_child"), { variant: "warning" });
      return;
    }

    if (event === "Sleep") {
      data.from = dayjs(sleepFrom).format("hh:mm A").toString();
      data.to = dayjs(sleepTo).format("hh:mm A").toString();
      if (sleepFrom && sleepTo) {
        let diff = sleepTo.diff(sleepFrom, "minute");
        if (diff < 0) diff += 1440;
        data.duration = (diff / 60).toString();
      }
    }

    setSubmitting(true);

    if (isEditing) {
      const payload = new FormData();
      payload.append("childId", childrenSelected as string);
      payload.append("eventType", eventTypeId as string);
      payload.append("infos", JSON.stringify(data));
      images.forEach((file) => payload.append("images", file));
      imgs.forEach((path) => payload.append("imgs", path));

      Axios.put(`/api/events/${editItem.id}`, payload)
        .then(() => {
          enqueueSnackbar(t("timeline.event_updated"), { variant: "success" });
          dispatch(invalidateEvents());
          onClose();
        })
        .catch((err) => {
          if (err?.response?.status === 400) {
            enqueueSnackbar(err.response.data.message, { variant: "error" });
          }
        })
        .finally(() => setSubmitting(false));
    } else {
      createEvent({
        childId: childrenSelected as string,
        eventType: eventTypeId,
        infos: data,
        images,
      })
        .then(({ data }) => {
          enqueueSnackbar(t("timeline.event_added"), { variant: "success" });
          dispatch(addEvent(data));
          onClose();
        })
        .catch((err) => {
          if (err?.response?.status === 400) {
            enqueueSnackbar(err.response.data.message, { variant: "error" });
          }
        })
        .finally(() => setSubmitting(false));
    }
  };

  const handleImageAdd = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length) {
      const files: File[] = [];
      for (let i = 0; i < e.target.files.length; i++)
        files.push(e.target.files[i]);
      setImages((prev) => [...prev, ...files]);
    }
  };

  const allImages = useMemo(() => {
    const existing = imgs.map((src, idx) => ({
      type: "url" as const,
      src,
      idx,
    }));
    const newOnes = images.map((file, idx) => ({
      type: "file" as const,
      src: URL.createObjectURL(file),
      idx,
    }));
    return [...existing, ...newOnes];
  }, [imgs, images]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      TransitionComponent={SlideUp}
      PaperProps={{
        sx: { borderRadius: 3, overflow: "hidden" },
      }}
    >
      {/* Header with gradient */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
          color: "common.white",
          px: 3,
          py: 2.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box display="flex" alignItems="center" gap={1.5}>
          <Typography variant="h6" fontWeight={700}>
            {isEditing ? t("labels.edit") : t(`posts.add.${eventLower}`)}
          </Typography>
          <Chip
            label={t(`posts.${eventLower}`)}
            size="small"
            sx={{
              bgcolor: alpha(theme.palette.common.white, 0.2),
              color: "common.white",
              fontWeight: 600,
              fontSize: 11,
            }}
          />
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ color: "common.white" }}
        >
          <Close />
        </IconButton>
      </Box>

      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <DialogContent sx={{ px: 3, py: 3 }}>
          {/* Child selector */}
          <Typography
            variant="caption"
            color="text.secondary"
            fontWeight={600}
            sx={{
              mb: 1,
              display: "block",
              textTransform: "uppercase",
              letterSpacing: 0.5,
            }}
          >
            {t("timeline.child")}
          </Typography>
          <FormControl fullWidth size="small" sx={{ mb: 3 }}>
            <Select
              displayEmpty
              value={childrenSelected ?? ""}
              onChange={(e) => setChildrenSelected(e.target.value as string)}
              input={<OutlinedInput />}
              MenuProps={childPickerMenuProps}
              renderValue={(value) => {
                if (!value) {
                  return (
                    <Typography color="text.disabled" variant="body2">
                      {t("placeholders.select_child")}
                    </Typography>
                  );
                }
                const c = child.find((ch) => ch.id === value);
                if (!c) return null;
                return (
                  <Box display="flex" alignItems="center" gap={1}>
                    <ChildAvatar child={c} size={24} fontSize={11} />
                    <Typography variant="body2">
                      {`${c.firstName} ${c.lastName}`}
                    </Typography>
                  </Box>
                );
              }}
              sx={{
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "divider",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "primary.light",
                },
                borderRadius: 2,
              }}
            >
              {(!child || child.length === 0) && (
                <MenuItem
                  disabled
                  sx={{
                    py: 3,
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  <ChildCare sx={{ fontSize: 28, color: "action.disabled" }} />
                  <Typography variant="body2" color="text.disabled">
                    {t("timeline.no_children")}
                  </Typography>
                </MenuItem>
              )}
              {child?.map((c) => {
                const isSelected = childrenSelected === c.id;
                return (
                  <MenuItem
                    key={c.id}
                    value={c.id}
                    sx={childPickerMenuItemSx(isSelected)}
                  >
                    <ChildPickerOption child={c} isSelected={isSelected} />
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>

          {/* Notes field (all except Meal) */}
          {showNotes && (
            <TextField
              label={t("notes")}
              multiline
              rows={4}
              fullWidth
              size="small"
              sx={{
                mb: 2,
                "& .MuiOutlinedInput-root": { borderRadius: 2 },
              }}
              placeholder={t(`posts.add.${eventLower}`) + "..."}
              {...register("notes")}
            />
          )}

          {/* Nappy type */}
          {event === "Nappy" && (
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel id="nappy-type-label">{t("posts.nappy")}</InputLabel>
              <Select
                labelId="nappy-type-label"
                label={t("posts.nappy")}
                defaultValue=""
                sx={{ borderRadius: 2 }}
                {...register("nappy", { required: true })}
              >
                <MenuItem value="Wet">{t("posts.wet")}</MenuItem>
                <MenuItem value="Soiled">{t("posts.soiled")}</MenuItem>
              </Select>
            </FormControl>
          )}

          {/* Sleep time range */}
          {event === "Sleep" && (
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6}>
                  <DesktopTimePicker
                    label={t("labels.from")}
                    value={sleepFrom}
                    onChange={(v) => setSleepFrom(v)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        fullWidth
                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={6}>
                  <DesktopTimePicker
                    label={t("labels.to")}
                    value={sleepTo}
                    onChange={(v) => setSleepTo(v)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        fullWidth
                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </LocalizationProvider>
          )}

          {/* Meal fields */}
          {event === "Meal" && (
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel id="meal-type-label">
                    {t("inputs.type")}
                  </InputLabel>
                  <Select
                    labelId="meal-type-label"
                    label={t("inputs.type")}
                    defaultValue=""
                    sx={{ borderRadius: 2 }}
                    {...register("type", { required: true })}
                  >
                    <MenuItem value="Breakfast">
                      {t("posts.breakfast")}
                    </MenuItem>
                    <MenuItem value="Lunch">{t("posts.lunch")}</MenuItem>
                    <MenuItem value="Dinner">{t("posts.dinner")}</MenuItem>
                    <MenuItem value="Snack">{t("posts.snack")}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel id="meal-qty-label">
                    {t("posts.quantity")}
                  </InputLabel>
                  <Select
                    labelId="meal-qty-label"
                    label={t("posts.quantity")}
                    defaultValue=""
                    sx={{ borderRadius: 2 }}
                    {...register("quantity", { required: true })}
                  >
                    <MenuItem value="All">{t("posts.all")}</MenuItem>
                    <MenuItem value="Most">{t("posts.most")}</MenuItem>
                    <MenuItem value="Some">{t("posts.some")}</MenuItem>
                    <MenuItem value="Nothing">{t("posts.nothing")}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label={t("notes")}
                  multiline
                  rows={3}
                  fullWidth
                  size="small"
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                  {...register("notes")}
                />
              </Grid>
            </Grid>
          )}

          {/* Photos */}
          {showPhotos && (
            <Box>
              {allImages.length > 0 && (
                <ImageList
                  cols={3}
                  gap={8}
                  sx={{
                    maxHeight: 240,
                    mb: 1.5,
                    borderRadius: 2,
                    overflow: "hidden",
                  }}
                >
                  {allImages.map((img) => (
                    <ImageListItem
                      key={`${img.type}-${img.idx}`}
                      sx={{ borderRadius: 1, overflow: "hidden" }}
                    >
                      <IconButton
                        sx={{
                          position: "absolute",
                          top: 4,
                          right: 4,
                          bgcolor: "rgba(0,0,0,0.5)",
                          color: "common.white",
                          "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
                          width: 24,
                          height: 24,
                        }}
                        size="small"
                        onClick={() => {
                          if (img.type === "url")
                            setImgs((prev) =>
                              prev.filter((_, i) => i !== img.idx),
                            );
                          else
                            setImages((prev) =>
                              prev.filter((_, i) => i !== img.idx),
                            );
                        }}
                      >
                        <Close sx={{ fontSize: 14 }} />
                      </IconButton>
                      <img
                        src={img.src}
                        alt={event}
                        style={{
                          objectFit: "cover",
                          height: "100%",
                          width: "100%",
                        }}
                      />
                    </ImageListItem>
                  ))}
                </ImageList>
              )}
              <Button
                component="label"
                variant="outlined"
                startIcon={<AddAPhoto />}
                size="small"
                fullWidth
                sx={{
                  borderRadius: 2,
                  borderStyle: "dashed",
                  py: 1.5,
                  color: "text.secondary",
                  borderColor: "divider",
                  "&:hover": {
                    borderColor: "primary.main",
                    bgcolor: alpha(theme.palette.primary.main, 0.04),
                  },
                }}
              >
                {t("buttons.upload")}
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  hidden
                  onChange={handleImageAdd}
                />
              </Button>
            </Box>
          )}
        </DialogContent>

        {/* Actions */}
        <Box sx={{ px: 3, pb: 3, display: "flex", gap: 1.5 }}>
          <Button
            onClick={onClose}
            variant="outlined"
            fullWidth
            sx={{
              borderRadius: 2,
              py: 1.2,
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            {t("buttons.cancel")}
          </Button>
          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={submitting}
            sx={{
              borderRadius: 2,
              py: 1.2,
              textTransform: "none",
              fontWeight: 600,
              boxShadow: "none",
              "&:hover": { boxShadow: 2 },
            }}
          >
            {submitting ? (
              <CircularProgress size={20} color="inherit" />
            ) : isEditing ? (
              t("buttons.save")
            ) : (
              t("post")
            )}
          </Button>
        </Box>
      </form>
    </Dialog>
  );
};

export default EventDialog;
